import type { ComponentProps, PropsWithChildren, ReactNode } from 'react';

import type { PressEvent } from 'react-aria-components/Button';

import { Button } from '../button/button';
import {
  Dialog,
  DialogActionButtons,
  DialogContent,
  DialogContentText,
  DialogHeading,
  type DialogSize,
} from '../dialog/dialog';
import { Modal } from '../modal/modal';

export type ConfirmDialogProps = PropsWithChildren<{
  cancelButtonLabel: ReactNode;
  confirmButtonLabel: ReactNode;
  headingIcon?: ReactNode;
  isConfirmDisabled?: boolean;
  isConfirmPending?: boolean;
  onCancel?: () => void;
  onConfirm?: (event: PressEvent) => void;
  size?: DialogSize;
  title: ReactNode;
  variant?: 'default' | 'warning' | 'destructive';
  confirmButtonIcon?: ReactNode;
}> &
  ComponentProps<typeof Modal>;

const variantToButtonVariant = {
  default: 'primary',
  warning: 'destructive',
  destructive: 'destructive',
} as const;

/**
 * Confirm dialog component.
 *
 * A generic dialog to prompt whenever a user needs to confirm an action.
 *
 * @example
 * ```tsx
 * <ConfirmDialog
 *   title="Delete item"
 *   confirmButtonLabel="Delete"
 *   cancelButtonLabel="Cancel"
 *   variant="warning"
 *   onCancel={handleClose}
 *   onConfirm={handleClose}
 *   headingIcon={<Icon name="deletion_delete" />}
 * >
 *   Proceeding with this action will remove all related comments posted on this item.
 * </ConfirmDialog>
 * ```
 */
export const ConfirmDialog = ({
  cancelButtonLabel,
  children,
  confirmButtonLabel,
  headingIcon,
  isConfirmDisabled,
  isConfirmPending,
  onCancel,
  onConfirm,
  onOpenChange,
  size = 's',
  title,
  variant = 'default',
  ...props
}: ConfirmDialogProps) => {
  const buttonVariant = variantToButtonVariant[variant];
  const hasPlainTextChildren =
    typeof children === 'string' || typeof children === 'number';

  // React Aria warns when a modal nested in a trigger is handed an
  // `onOpenChange`, so only attach one when there is something to report.
  const handleOpenChange =
    onCancel || onOpenChange
      ? (isOpen: boolean) => {
          if (!isOpen) {
            onCancel?.();
          }
          onOpenChange?.(isOpen);
        }
      : undefined;

  return (
    <Modal {...props} onOpenChange={handleOpenChange} isDismissible>
      <Dialog size={size} role="alertdialog">
        <DialogHeading startIcon={headingIcon} variant={variant} isDismissible>
          {title}
        </DialogHeading>
        <DialogContent>
          {hasPlainTextChildren ? (
            <DialogContentText>{children}</DialogContentText>
          ) : (
            children
          )}
        </DialogContent>
        <DialogActionButtons>
          <Button slot="close" variant="tertiary" size="l">
            {cancelButtonLabel}
          </Button>
          <Button
            variant={buttonVariant}
            size="l"
            onPress={onConfirm}
            autoFocus
            isDisabled={isConfirmDisabled}
            isPending={isConfirmPending}
            icon={props.confirmButtonIcon}
          >
            {confirmButtonLabel}
          </Button>
        </DialogActionButtons>
      </Dialog>
    </Modal>
  );
};
